import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IDisplayValue } from '../../ts/Interfaces';
const compareObjects = require('fast-deep-equal');

export interface IValueSelectProperties {
    value: string;
    values: IDisplayValue[];
    emptyListPlaceHolder?: string;
    readonly?: boolean;

    onChange(value: string): void;
    onApply(value: string): void;
    onClose(): void;
}

interface IValueSelectState {
    value: string;
    focusedItemIndex?: number;
}

export default class ValueSelect extends React.Component<IValueSelectProperties, IValueSelectState> {
    private references: {
        searchField?: HTMLInputElement;
    } = {};

    constructor(props) {
        super(props);

        this.state = this.getState(this.props.value);
    }

    getState(value: string): IValueSelectState {
        const values = this.props.values || [];

        //Initialize for correct comparision.
        let oldFocusedItemIndex: number = null;
        let newFocusedItemIndex: number = null;

        let oldValue: string;
        let newValue = value || '';

        if (this.state != null) {
            oldFocusedItemIndex = this.state.focusedItemIndex;

            if (oldFocusedItemIndex != null && oldFocusedItemIndex < values.length) {
                newFocusedItemIndex = oldFocusedItemIndex;
            }

            oldValue = this.state.value || '';
        }

        if (newFocusedItemIndex == null) {
            if (newValue.length > 0) {
                for (let index = 0; index < values.length; index++) {
                    if (values[index].value === newValue) {
                        newFocusedItemIndex = index;
                        break;
                    }
                }
            }

            if (newFocusedItemIndex == null && this.props.readonly === true && values.length > 0) {
                newFocusedItemIndex = 0;
            }
        }

        let result: IValueSelectState = {
            value: newValue,
            focusedItemIndex: newFocusedItemIndex
        };

        if (oldFocusedItemIndex !== newFocusedItemIndex) {
            result = this.getNewStateOnFocusChange(newFocusedItemIndex, result);

            //Calling onChange so often is needed because Apply button is out of the component.
            //To make it work a new value must be passed up to the parent component.
            if (oldValue !== result.value) {
                this.processOnChange(result.value);
            }
        }

        return result;
    }

    private getNewStateOnFocusChange(focusedItemIndex: number, state: IValueSelectState): IValueSelectState {
        const result = $.extend(true, {}, state);

        result.focusedItemIndex = focusedItemIndex;

        const { values } = this.props;

        if (focusedItemIndex != null && values != null && values.length > 0) {
            const focusedItem = values[focusedItemIndex];

            result.value = focusedItem.value;
        }

        return result;
    }

    componentDidMount() {
        this.setFocusToSearchField();
    }

    componentDidUpdate(prevProps: IValueSelectProperties, prevState: IValueSelectState) {
        if (!compareObjects(prevProps, this.props)) {
            this.setState(this.getState(this.props.value), () => {
                this.setFocusToSearchField();
            });
        } else {
            this.setFocusToSearchField();
        }
    }

    private setFocusToSearchField() {
        const input = this.references.searchField;
        if (input != null) {
            input.focus();
        }
    }

    private moveFocus(direction: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT') {
        const { values } = this.props;

        if (values == null || values.length === 0) {
            return;
        }

        let focusedItemID = this.state.focusedItemIndex;

        switch (direction) {
            case 'DOWN':
                if (focusedItemID == null) {
                    focusedItemID = 0;
                } else if (focusedItemID < values.length - 1) {
                    ++focusedItemID;
                }
                break;

            case 'UP':
                if (focusedItemID != null && focusedItemID > 0) {
                    --focusedItemID;
                } else if (this.props.readonly !== true) {
                    focusedItemID = undefined;
                }
                break;
        }

        if (focusedItemID !== this.state.focusedItemIndex) {
            this.setState(this.getNewStateOnFocusChange(focusedItemID, this.state), () => {
                this.processOnChange();
            });
        }
    }

    handleKeyDown = (e) => {
        switch (e.code) {
            case 'Escape': //Escape.
                this.props.onClose();
                break;

            case 'Enter': //Enter.
                this.processEnterPress();
                break;

            case 'ArrowUp':
                e.preventDefault();
                this.moveFocus('UP');
                break;

            case 'ArrowDown':
                e.preventDefault();
                this.moveFocus('DOWN');
                break;

            default:
        }
    };

    processEnterPress() {
        const { focusedItemIndex: focusedItemID } = this.state;

        if (focusedItemID != null && focusedItemID >= 0) {
            this.setState(
                {
                    value: this.props.values[focusedItemID].value
                },
                () => {
                    this.processOnApply();
                }
            );
        } else {
            this.processOnApply();
        }
    }

    getOnItemSelectHandler(index: number) {
        return (e) => {
            const item = this.props.values[index];

            this.setState(
                {
                    value: item.value,
                    focusedItemIndex: index
                },
                () => {
                    this.processOnChange();
                }
            );
        };
    }

    getOnItemDoubleClickHandler(index: number) {
        return (e) => {
            this.processOnApply();
        };
    }

    normailzeValue(value: string) {
        let result: string = null;

        if (value.length > 0) {
            result = value.trim();

            if (result.length === 0) {
                result = null;
            }
        }

        return result;
    }

    processOnApply() {
        this.props.onApply(this.normailzeValue(this.state.value));
    }

    handleContainerClick = (e) => {
        e.stopPropagation();
        e.preventDefault();

        this.setFocusToSearchField();
    };

    processOnChange(value?: string) {
        if (this.props.onChange != null) {
            this.props.onChange(this.normailzeValue(value !== undefined ? value : this.state.value));
        }
    }

    handleOnEditorChange = (e) => {
        const value = this.references.searchField.value.trimLeft();

        this.setState(this.getState(value), () => {
            if (this.props.readonly !== true) {
                this.processOnChange();
            }
        });
    };

    render() {
        const items: React.JSX.Element[] = [];
        const isReadonly = this.props.readonly === true;
        const value = this.state.value;

        _.each(this.props.values, (item, index) => {
            items.push(
                <li
                    key={index}
                    className={
                        'item' +
                        (this.state.focusedItemIndex === index ? ' focused' : '') +
                        (value === item.value ? ' selected' : '')
                    }
                    onClick={this.getOnItemSelectHandler(index)}
                    onDoubleClick={this.getOnItemDoubleClickHandler(index)}
                >
                    <span>{item.displayValue || item.value}</span>
                </li>
            );
        });

        if (items.length === 0) {
            items.push(
                <li key="empty-item" className="empty-item">
                    <span>{this.props.emptyListPlaceHolder || 'No item is found'}</span>
                </li>
            );
        }

        return (
            <div className="value-select" onClick={this.handleContainerClick} onKeyDown={this.handleKeyDown}>
                <div className="editor-element">
                    <input
                        type="text"
                        className="editor-input"
                        ref={(input) => {
                            this.references.searchField = input;
                        }}
                        value={this.state.value}
                        placeholder={'Start typing here'}
                        readOnly={isReadonly}
                        spellCheck={false}
                        autoCorrect="off"
                        autoCapitalize="off"
                        autoComplete="off"
                        onInput={this.handleOnEditorChange}
                        onChange={() => {}}
                    />
                </div>

                <div className="content-body">
                    <ul className="value-list list-unstyled">{items}</ul>
                </div>
            </div>
        );
    }
}
